import anime from 'animejs/lib/anime.es.js';
import ScrollMagic from 'scrollmagic';
import {
  getCssVariable,
  isIOS,
  isLandscape,
  prefersReducedMotion,
} from './utils';
import { FEATURED_ITEM_COUNT } from './constants';

let controller;

const onProgress = (timeline) => (args) => {
  const { progress } = args;
  timeline.seek(progress * timeline.duration);
};

const initLogo = () => {
  const endFontSize = isLandscape() ? window.innerWidth : window.innerHeight;

  const timeline = anime.timeline({ autoplay: false, duration: 10000 });

  timeline
    .add(
      {
        targets: 'svg text#main',
        fontSize: `${endFontSize}px`,
        easing: 'linear',
      },
      1
    )
    .add(
      {
        targets: 'svg tspan#land',
        fontSize: `${endFontSize}px`,
        dx: `-${endFontSize / 7.5}px`,
        dy: 0,
        easing: 'linear',
      },
      1
    )
    .add(
      {
        targets: 'svg tspan#software',
        fontSize: `${endFontSize / 15}px`,
        dx: '0px',
        dy: '0px',
        easing: 'linear',
      },
      1
    )
    .add(
      {
        targets: '.landing-intro',
        opacity: [1, 0],
        easing: 'linear',
        duration: 2500,
      },
      1
    );

  const scene = new ScrollMagic.Scene({
    offset: window.innerHeight / 2,
    triggerElement: 'section.landing',
    duration: window.innerHeight,
  });
  // ScrollMagic bug on mobile
  if (!isIOS()) {
    scene.setPin('section.landing');
  }
  const seek = onProgress(timeline);
  const landing = document.querySelector('section.landing');
  scene
    .on('progress', (args) => {
      seek(args);
      // Once faded, the intro must not stay clickable or focusable, and the
      // scroll cue's own keyframes would keep it visible against an inline
      // opacity; a class hides both outright.
      landing.classList.toggle('is-faded', args.progress >= 0.25);
    })
    .addTo(controller);
};

// Headings fade in as they enter; they are no longer pinned, which used to
// cost half a viewport of scrolling per heading.
const initHeadings = (sectionConfigs) => {
  sectionConfigs.forEach((_, i) => {
    const target = `.section-${i} .section-heading`;
    const timeline = anime.timeline({ autoplay: false, duration: 10000 });

    timeline
      .add({ targets: target, opacity: [0, 1], easing: 'linear' }, 1)
      .add({ targets: target, translateX: [10, 0] }, 1);

    new ScrollMagic.Scene({
      triggerElement: target,
      triggerHook: 0.9,
      duration: window.innerHeight * 0.3,
    })
      .on('progress', onProgress(timeline))
      .addTo(controller);
  });
};

const initArticles = (sectionConfigs) => {
  sectionConfigs.forEach(({ items }, sectionIdx) => {
    items.slice(0, FEATURED_ITEM_COUNT).forEach((_, articleIdx) => {
      const target = `.section-${sectionIdx} .article-${articleIdx}`;

      const primary = getCssVariable('--global__color_primary');
      const secondary = getCssVariable('--global__color_secondary');

      const timeline = anime.timeline({ autoplay: false, duration: 15000 });

      timeline
        .add(
          {
            targets: `${target} figcaption`,
            borderColor: [primary, secondary],
          },
          2
        )
        .add(
          {
            targets: `${target} figcaption h4`,
            color: [primary, secondary],
          },
          2
        )
        .add(
          {
            targets: `${target} img`,
            opacity: [1, 0.25],
            easing: 'linear',
          },
          1
        )
        .add(
          {
            targets: `${target} figcaption`,
            backgroundColor: ['transparent', primary],
            easing: 'linear',
          },
          2
        )
        .add(
          {
            targets: `${target} .info-block svg rect`,
            strokeDashoffset: [(...args) => anime.setDashoffset(...args), 1],
            easing: 'linear',
            delay: 9000,
          },
          3
        )
        .add(
          {
            // A clip reveal keeps the text at its final width the whole time,
            // so it never reflows while scrolling.
            targets: `${target} .info-text`,
            clipPath: ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'],
            easing: 'linear',
            delay: 11000,
          },
          4
        );

      new ScrollMagic.Scene({
        offset: window.innerHeight / 2,
        triggerElement: target,
        duration: window.innerHeight * 0.6,
      })
        .setPin(target)
        .on('progress', onProgress(timeline))
        .addTo(controller);
    });
  });
};

const initOverflowBlocks = (sectionConfigs) => {
  sectionConfigs.forEach((_, sectionIdx) => {
    const target = `.section-${sectionIdx} .overflow-block`;
    const el = document.querySelector(target);
    if (!el) return;

    const timeline = anime.timeline({ autoplay: false, duration: 10000 });

    timeline
      .add(
        {
          targets: `${target} .overflow-border svg rect`,
          strokeDashoffset: [(...args) => anime.setDashoffset(...args), 1],
          easing: 'linear',
        },
        1
      )
      .add(
        {
          targets: `${target} .overflow-item`,
          opacity: [0, 1],
          translateY: [20, 0],
          easing: 'easeOutQuad',
          delay: anime.stagger(150),
        },
        3000
      );

    new ScrollMagic.Scene({
      triggerElement: target,
      triggerHook: 0.7,
      duration: window.innerHeight * 0.6,
    })
      .on('progress', onProgress(timeline))
      .addTo(controller);
  });
};

const destroyController = () => {
  if (controller) {
    controller.destroy(true);
    controller = null;
  }
};

export default (config) => {
  const { sections } = config;
  destroyController();

  // Static layout for people who asked for less motion: every element sits in
  // its final state via the `reduced-motion` styles, no scroll scenes at all.
  const reduced = prefersReducedMotion();
  document.documentElement.classList.toggle('reduced-motion', reduced);
  if (reduced) {
    // A previous logo timeline may have faded these out inline.
    document
      .querySelectorAll('.landing-intro, .scroll-cue')
      .forEach((el) => el.style.removeProperty('opacity'));
    return config;
  }

  controller = new ScrollMagic.Controller();
  initLogo();
  sections.forEach(({ items }) => {
    initHeadings(items);
    initArticles(items);
    initOverflowBlocks(items);
  });
  return config;
};
